import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { CaretDown } from '@phosphor-icons/react';
import { Inline, LinkButton, Stack, Text, textVariants } from '@etg/wings';
import { ExpandableContent, ScreenReaderPrice } from '@eti/components';
import { price as priceUtil, constants } from '@eti/utils';
import { css, cx, etiColors } from '@eti/styles';
import { useToggle } from '../../../common/custom-hooks/toggle';
import useId from '../hooks/useId';
import { CartRow, Price } from './CartComponents';
import CartBreakdownRowAvailability from './CartBreakdownRowAvailability';

const priceBreakdownStyles = css`
  color: ${etiColors.grayScale[10]};
  font-size: 0.75rem;
`;

const toggleStyles = css`
  align-items: center;
  display: flex;
  gap: 6px;
`;

const iconStyles = (isExpanded) => css`
  transform: rotateX(${isExpanded ? '180deg' : '0deg'});
  transition: transform 0.2s ease-in-out;
`;

const listStyles = css`
  list-style: none;
`;

const airportListItemStyles = css`
  margin-inline-start: 8px;
`;

export const RowToggle = ({ children, dataTestId, id, isExpanded, onClick }) => (
  <LinkButton
    aria-controls={id}
    aria-describedby={`${dataTestId}-price`}
    aria-expanded={isExpanded}
    className={toggleStyles}
    data-testid={dataTestId}
    onClick={onClick}
  >
    <Inline noWrap spacing={4}>
      {children}
      <CaretDown
        aria-hidden
        className={iconStyles(isExpanded)}
        color="currentColor"
        size={20}
        weight="light"
      />
    </Inline>
  </LinkButton>
);

RowToggle.propTypes = {
  children: PropTypes.node,
  dataTestId: PropTypes.string,
  id: PropTypes.string,
  isExpanded: PropTypes.bool,
  onClick: PropTypes.func,
};

const CartToggleableRow = ({ data, dataTestId, price, title, titleStyle = '' }) => {
  const { isOpen, handleToggle } = useToggle(false);
  const uniqueId = useId();
  const scrollableDivRef = React.useRef(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        scrollableDivRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 100);
    }
  }, [isOpen]);

  return (
    <div ref={scrollableDivRef}>
      <CartRow className={titleStyle} data-testid={dataTestId}>
        {data ? (
          <RowToggle
            dataTestId={`${dataTestId}-toggle`}
            id={uniqueId}
            isExpanded={isOpen}
            onClick={handleToggle}
          >
            {title}
          </RowToggle>
        ) : (
          <Text>{title}</Text>
        )}
        {price && (
          <>
            <Price aria-hidden data-testid={`${dataTestId}-price`}>
              {priceUtil.formatPrice(price)}
            </Price>
            <ScreenReaderPrice
              id={`${dataTestId}-toggle-price`}
              price={priceUtil.formatPrice(price)}
            />
          </>
        )}
      </CartRow>
      <ExpandableContent
        className={listStyles}
        id={uniqueId}
        isExpanded={data && isOpen}
        role="list"
      >
        {data?.map((row, index) => (
          <Stack as="li" key={`subsection-${index}`} role="listitem" spacing={0}>
            <CartRow
              className={cx(priceBreakdownStyles, constants.MOUSE_FLOW_CLASS_NAMES)}
              data-testid={`${dataTestId}-dataRow`}
            >
              <Text variant={textVariants._12}>{row.label || row.name}</Text>
              {row.value && (
                <>
                  <Price aria-hidden data-testid={`${dataTestId}-dataRow-price`}>
                    {priceUtil.formatPrice(row.value)}
                  </Price>
                  <ScreenReaderPrice price={priceUtil.formatPrice(row.value)} />
                </>
              )}
            </CartRow>
            {row.availability && row.availability.hasAvailability && (
              <CartBreakdownRowAvailability
                availability={row.availability}
                className={airportListItemStyles}
                dataTestId={dataTestId}
              />
            )}
          </Stack>
        ))}
      </ExpandableContent>
    </div>
  );
};

CartToggleableRow.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  dataTestId: PropTypes.string,
  price: PropTypes.number,
  title: PropTypes.string,
  titleStyle: PropTypes.string,
};

export default CartToggleableRow;
